import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function Header({ onApplyClick }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/#" + id);
            return;
        }
        const el = document.getElementById(id);
        if (el) {
            window.scrollTo({ top: el.offsetTop - 72, behavior: "smooth" });
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all ${
                scrolled ? "glass border-b border-slate-200" : "bg-moebus-deep/80 backdrop-blur-md border-b border-white/5"
            }`}
        >
            <div className="container mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
                <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
                    <img
                        src="https://customer-assets.emergentagent.com/job_moebus-karriere/artifacts/15h4op5x_moebus_logo_fuer_wwww_kl%20Final%20Done-2.svg"
                        alt="Möbus Umweltschutz GmbH"
                        className="h-12 md:h-14 w-auto object-contain"
                    />
                    <div className="leading-tight hidden sm:block">
                        <div className={`text-[10px] md:text-xs tracking-widest uppercase font-semibold ${scrolled ? "text-moebus-muted" : "text-emerald-400"}`}>
                            Karriere-Portal
                        </div>
                        <div className={`font-heading font-medium text-xs md:text-sm ${scrolled ? "text-moebus-deep" : "text-white"}`}>
                            Möbus Umweltschutz GmbH
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
                    <button
                        data-testid="nav-about"
                        onClick={() => scrollTo("ueber-uns")}
                        className={`link-underline ${scrolled ? "text-slate-700 hover:text-moebus-deep" : "text-white/80 hover:text-white"}`}
                    >
                        Über uns
                    </button>
                    <button
                        data-testid="nav-services"
                        onClick={() => scrollTo("was-wir-machen")}
                        className={`link-underline ${scrolled ? "text-slate-700 hover:text-moebus-deep" : "text-white/80 hover:text-white"}`}
                    >
                        Was wir machen
                    </button>
                    <button
                        data-testid="nav-benefits"
                        onClick={() => scrollTo("was-wir-bieten")}
                        className={`link-underline ${scrolled ? "text-slate-700 hover:text-moebus-deep" : "text-white/80 hover:text-white"}`}
                    >
                        Was wir bieten
                    </button>
                    <button
                        data-testid="nav-jobs"
                        onClick={() => scrollTo("stellen")}
                        className={`link-underline ${scrolled ? "text-slate-700 hover:text-moebus-deep" : "text-white/80 hover:text-white"}`}
                    >
                        Stellen
                    </button>
                    <button
                        data-testid="nav-apply"
                        onClick={() => scrollTo("bewerbung")}
                        className="bg-moebus-green hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-md transition-colors"
                    >
                        Jetzt bewerben
                    </button>
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className={`md:hidden p-2 -mr-2 ${scrolled ? "text-moebus-deep" : "text-white"}`}
                    aria-label="Menü öffnen"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-slate-200">
                    <div className="container mx-auto px-4 py-4 flex flex-col gap-3 text-base font-medium">
                        <button onClick={() => scrollTo("ueber-uns")} className="text-left py-2 text-slate-700">
                            Über uns
                        </button>
                        <button onClick={() => scrollTo("was-wir-machen")} className="text-left py-2 text-slate-700">
                            Was wir machen
                        </button>
                        <button onClick={() => scrollTo("was-wir-bieten")} className="text-left py-2 text-slate-700">
                            Was wir bieten
                        </button>
                        <button onClick={() => scrollTo("stellen")} className="text-left py-2 text-slate-700">
                            Stellen
                        </button>
                        <button
                            onClick={() => scrollTo("bewerbung")}
                            className="bg-moebus-green text-white font-medium px-5 py-3 rounded-md mt-2"
                        >
                            Jetzt bewerben
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
